import React, { useState, useEffect, useCallback, useMemo } from "react";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import Sidebar from "../../../components/layout/Sidebar";
import Select from "react-select";
import { Row, Col, Form } from "react-bootstrap";
import axios from "axios";
import { BASE_URL } from "../../../env";
import { IoSearch } from "react-icons/io5";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TransferCroMemberWise = () => {
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [fromCroOptions, setFromCroOptions] = useState([]);
  const [toCroOptions, setToCroOptions] = useState([]);
  const [selectedFromCro, setSelectedFromCro] = useState(null);
  const [selectedToCro, setSelectedToCro] = useState(null);
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const transformCroData = (data) =>
    data.map((item) => ({
      value: item.id,
      label: item.username,
      branchId: item.branchId,
    }));

  const getToCroData = async (branchId, croId) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/getCroData`, {
        params: { branchId, croId, transferType: "member" },
      });
      if (response.data.message) {
        setToCroOptions(transformCroData(response.data.message));
      } else {
        setToCroOptions([]);
      }
      if (response.data.memberData) {
        setMembers(response.data.memberData);
      } else {
        setMembers([]);
      }
    } catch (error) {
      console.error("Error fetching To CRO data:", error);
      toast.error("Error loading CRO members");
    }
  };

  const getFromCroData = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/getCroData`);
      if (response.data.message) {
        setFromCroOptions(transformCroData(response.data.message));
      } else {
        setFromCroOptions([]);
      }
    } catch (error) {
      console.error("Error fetching From CRO data:", error);
    }
  }, []);

  useEffect(() => {
    getFromCroData();
  }, [getFromCroData]);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  const handleFromCroChange = (selectedOption) => {
    setSelectedFromCro(selectedOption);
    setSelectedToCro(null);
    setSelectedMembers([]);
    setSearchTerm("");
    if (selectedOption) {
      getToCroData(selectedOption.branchId, selectedOption.value);
    } else {
      setToCroOptions([]);
      setMembers([]);
    }
  };

  const handleToCroChange = (selectedOption) => {
    setSelectedToCro(selectedOption);
  };

  const handleMemberChange = (memberId) => {
    if (selectedMembers.includes(memberId)) {
      setSelectedMembers(selectedMembers.filter((id) => id !== memberId));
    } else {
      setSelectedMembers([...selectedMembers, memberId]);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedMembers(filteredMembers.map((m) => m.id));
    } else {
      setSelectedMembers([]);
    }
  };

  const handleReset = () => {
    setSelectedFromCro(null);
    setSelectedToCro(null);
    setToCroOptions([]);
    setMembers([]);
    setSelectedMembers([]);
    setSearchTerm("");
    getFromCroData();
  };

  const handleSubmit = async () => {
    if (!selectedFromCro?.value) {
      toast.warning("Please select a 'From' CRO.");
      return;
    }
    if (!selectedToCro?.value) {
      toast.warning("Please select a 'To' CRO.");
      return;
    }
    if (selectedMembers.length === 0) {
      toast.warning("Please select at least one member.");
      return;
    }

    try {
      const response = await axios.post(`${BASE_URL}/api/transferCro`, {
        fromCroId: selectedFromCro.value,
        toCroId: selectedToCro.value,
        transferType: "member",
        selectedMembers: selectedMembers,
      });
      if (response.data.message) {
        toast.success("Members transferred successfully!");
        setTimeout(() => {
          window.location.reload();
        }, 1200);
      }
    } catch (error) {
      console.error("Transfer error:", error);
      toast.error(error.response?.data?.error || "Failed to transfer members");
    }
  };

  const filteredMembers = useMemo(() => {
    if (!searchTerm.trim()) return members;
    const q = searchTerm.toLowerCase().trim();
    return members.filter((m) => {
      const name = (m.memberName || "").toLowerCase();
      const appId = (m.ApplicationId || "").toLowerCase();
      return name.includes(q) || appId.includes(q);
    });
  }, [members, searchTerm]);

  const isAllSelected =
    filteredMembers.length > 0 &&
    filteredMembers.every((m) => selectedMembers.includes(m.id));

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section inline-header">
            <h1 className="TableHeaderText m-0">Transfer CRO Member Wise</h1>
          </div>

          {/* CRO Selection Bar */}
          <div className="master-search-section py-3 px-4">
            <Row className="w-100 g-3 align-items-center">
              <Col xs={12} md={6}>
                <label className="form-label fw-semibold text-muted mb-1">
                  From CRO
                </label>
                <Select
                  value={selectedFromCro}
                  onChange={handleFromCroChange}
                  options={fromCroOptions}
                  placeholder="Select source CRO..."
                  isClearable
                  className="filter-dropdown"
                />
              </Col>
              <Col xs={12} md={6}>
                <label className="form-label fw-semibold text-muted mb-1">
                  To CRO
                </label>
                <Select
                  value={selectedToCro}
                  onChange={handleToCroChange}
                  options={toCroOptions}
                  placeholder="Select destination CRO..."
                  isClearable
                  isDisabled={!selectedFromCro}
                  className="filter-dropdown"
                />
              </Col>
            </Row>
          </div>

          {/* Member Selection Section */}
          <div className="master-list-section">
            {members.length > 0 ? (
              <div className="w-100">
                {/* Search & Select All Controls */}
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-3">
                    <Form.Check
                      type="checkbox"
                      id="select-all-members"
                      label={`Select All (${selectedMembers.length}/${filteredMembers.length})`}
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      className="fw-semibold"
                    />
                  </div>

                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      placeholder="Search members by name or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                    <span className="search-icon">
                      <IoSearch />
                    </span>
                  </div>
                </div>

                {/* Table list */}
                <div className="master-table-wrapper table-responsive">
                  <table className="mx-auto table table-striped table-list">
                    <thead>
                      <tr>
                        <th style={{ width: "60px" }}>Select</th>
                        <th style={{ width: "70px" }}>No</th>
                        <th>Member Name</th>
                        <th>Application ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMembers.length > 0 ? (
                        filteredMembers.map((member, index) => {
                          const isChecked = selectedMembers.includes(member.id);
                          return (
                            <tr
                              key={member.id}
                              onClick={() => handleMemberChange(member.id)}
                              style={{ cursor: "pointer" }}
                            >
                              <td onClick={(e) => e.stopPropagation()}>
                                <Form.Check
                                  type="checkbox"
                                  id={`member-${member.id}`}
                                  checked={isChecked}
                                  onChange={() => handleMemberChange(member.id)}
                                />
                              </td>
                              <td>{index + 1}</td>
                              <td className="fw-semibold text-start">
                                {member.memberName}
                              </td>
                              <td>{member.ApplicationId || "-"}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center py-4 text-muted">
                            No members match the search filter
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-5 text-muted">
                {selectedFromCro
                  ? "No members found for the selected CRO."
                  : "Please select a 'From CRO' to view and assign members."}
              </div>
            )}
          </div>

          {/* Action Footer */}
          <div className="master-pagination-wrapper py-3">
            <div className="d-flex justify-content-center align-items-center gap-3">
              <button
                type="button"
                className="btn btn-addCredential m-0"
                onClick={handleSubmit}
                disabled={selectedMembers.length === 0 || !selectedToCro}
              >
                Transfer Selected Members ({selectedMembers.length})
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleReset}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-center" />
    </section>
  );
};

export default TransferCroMemberWise;
